import type { ComponentType } from 'react';
import type { FileHandleRef, PlatformBridge, SearchMatch, ViewMode } from '@slate/core';
import type { ThemePref, ThemeName } from '../store/docStore';

export type CommandGroup =
  | 'File'
  | 'Tabs'
  | 'View'
  | 'Workspace'
  | 'Theme'
  | 'Reading'
  | 'Outline'
  | 'Tools'
  | 'Plugins'
  | 'Application'
  | 'Help';

export type CommandParam =
  | {
      name: string;
      kind: 'number';
      range?: [min: number, max: number];
      presets?: number[];
      displayUnit?: '%' | 'x';
    }
  | {
      name: string;
      kind: 'enum';
      values: { value: string; label?: string }[];
    }
  | {
      name: string;
      kind: 'path';
      pathMode: 'file' | 'dir' | 'any';
      rootSource: 'workspace' | 'home';
    }
  | { name: string; kind: 'tab' }
  | { name: string; kind: 'root' }
  | { name: string; kind: 'heading' }
  | { name: string; kind: 'shell' };

export type CommandArgs = Record<string, unknown>;

/** Lucide-icon-compatible component type (e.g. `Folder` from lucide-react). */
export type CommandIcon = ComponentType<{ size?: number; className?: string; 'aria-hidden'?: boolean }>;

export interface CommandCtx {
  platform: PlatformBridge;
  newUntitled: (text?: string) => string;
  activateDoc: (id: string) => void;
  requestCloseDocs: (ids: string[]) => void;
  toggleSplit: () => void;
  setViewMode: (mode: ViewMode) => void;
  toggleSidebar: () => void;
  toggleOutlinePinned: (pinned?: boolean) => void;
  setThemePref: (pref: ThemePref) => void;
  setTheme: (theme: ThemeName) => void;
  addWorkspaceRoot: (ref: FileHandleRef) => void;
  removeWorkspaceRoot: (id: string) => void;
  setReadingFontScale: (scale: number) => void;
  setReadingWidth: (width: 'narrow' | 'wide' | 'full') => void;
  setPendingJumpLine: (line: number | null) => void;
  /** Set (or clear, with `null`) a document's `/lang` override by id. */
  setDocLanguage: (docId: string, key: string | null) => void;
  /** Flip the active document's soft-wrap (per-document transient override). */
  toggleActiveDocWrap: () => void;
}

export interface CommandRunResult {
  keepOpen?: true;
}

export interface Command {
  id: string;
  name: string;
  synonyms?: string[];
  slash?: string;
  /** Alternative slash forms, e.g. `['/terminal']` for a `/cmd` command. */
  aliases?: string[];
  group: CommandGroup;
  icon?: CommandIcon;
  shortcut?: string;
  enabled: boolean;
  params?: CommandParam[];
  run: (ctx: CommandCtx, args: CommandArgs) => void | Promise<void> | CommandRunResult;
}

export type PaletteMode = 'empty' | 'mixed' | 'command' | 'outline' | 'help';

export interface PaletteInput {
  mode: PaletteMode;
  commandSlash?: string;
  args: string[];
  activeArgIndex: number;
  activeToken: string;
  query: string;
}

export type HitKind =
  | { kind: 'tab'; docId: string }
  | { kind: 'recent'; path: string; name: string }
  | { kind: 'command'; commandId: string; argsPreview?: CommandArgs; clampHint?: string }
  | { kind: 'docName'; docId: string }
  | { kind: 'docContent'; docId: string; line: number; snippet: string; matchRange: [number, number] }
  | { kind: 'wsName'; path: string; name: string; isDir: boolean }
  | { kind: 'wsContent'; path: string; name: string; line: number; snippet: string; matchRange: [number, number] }
  | { kind: 'pathEntry'; absPath: string; name: string; isDir: boolean }
  /**
   * Finalizer row pinned to the top of a path argument when the typed token
   * resolves to an existing directory: "Open this folder: <displayPath>".
   * Activation adds the directory as a workspace root (whether the parent
   * command was `/open` or `/open-folder`).
   */
  | { kind: 'openCurrentDir'; absPath: string; displayPath: string }
  | { kind: 'heading'; line: number; depth: number; text: string }
  | { kind: 'enumValue'; value: string; label: string }
  | {
      kind: 'numberPreset';
      /** The final (clamped) multiplier the command will receive. */
      value: number;
      displayUnit?: '%' | 'x';
      isCurrent?: boolean;
      /** When set, the renderer uses this verbatim (used for the LIVE-input
       *  preview to echo the user's scale: '2.3×', '30%', etc.). Presets omit
       *  this and the renderer formats from `value`. */
      displayLabel?: string;
      /** True when the live input was clamped to fit `range`; renderer shows a
       *  '→ <value>' hint after the typed value. */
      clamped?: boolean;
      /** Pre-clamp multiplier; used together with `clamped` for the hint. */
      raw?: number;
      /** How the user's input was interpreted, so the live-preview can echo. */
      parsedScale?: 'percent' | 'multiplier';
    }
  | { kind: 'shellCommand'; command: string; count?: number; literal?: boolean }
  | { kind: 'help'; prefix: string; description: string };

/**
 * Section labels used both as visual headers in the result list AND as the
 * primary key for mixed-mode sorting. Order in this union mirrors the rank
 * used by `CommandPalette`'s row sort — keep them aligned.
 *
 * For mixed-mode search the DEFAULT user-facing priority (highest → lowest) is:
 *   1. Active doc — name
 *   2. Open tabs — name
 *   3. Workspace folders
 *   4. Workspace files
 *   5. Active doc — content
 *   6. Open tabs — content
 *   7. Workspace content
 *
 * Users can override this in Settings → Command Palette; the seven buckets
 * above are the sortable items. The non-search sections (Commands, Recent,
 * Path, Outline, Enum, Numeric, Help) are fixed and not user-orderable.
 */
export type HitSection =
  | 'Commands'
  | 'Recent'
  | 'Active doc — name'
  | 'Open tabs'
  | 'Workspace folders'
  | 'Workspace files'
  | 'Active doc — content'
  | 'Open tabs content'
  | 'Workspace content'
  | 'Path'
  | 'Outline'
  | 'Help'
  | 'Enum'
  | 'Numeric'
  | 'Terminal';

/**
 * The seven mixed-mode buckets users can reorder. Treated as the canonical
 * set in both the settings UI and the runtime sort.
 */
export type SortableHitSection =
  | 'Active doc — name'
  | 'Open tabs'
  | 'Workspace folders'
  | 'Workspace files'
  | 'Active doc — content'
  | 'Open tabs content'
  | 'Workspace content';

export const SORTABLE_HIT_SECTIONS: readonly SortableHitSection[] = [
  'Active doc — name',
  'Open tabs',
  'Workspace folders',
  'Workspace files',
  'Active doc — content',
  'Open tabs content',
  'Workspace content',
];

export interface Hit {
  hit: HitKind;
  /** Ranking score; higher = better. */
  score: number;
}

/** Re-export from core for convenience. */
export type { SearchMatch };
